<script lang="ts" setup>
import dayjs from 'dayjs'

import ChooseWorkspaceModal from '~/components/ChooseWorkspaceModal.vue'

const {
  user,
  workspace,
} = useAuth()

const { t } = useI18n()
const now = useNow({ interval: 60_000 })

const workspaceModal = ref<InstanceType<typeof ChooseWorkspaceModal>>()

const amountOfDaysLeft = computed(() =>
  dayjs(workspace.value?.attributes.deletion_scheduled_to).diff(now.value, 'day'),
)

const { formatRelativeTime } = useIntl()
const timeLeftText = computed(() => formatRelativeTime(amountOfDaysLeft.value, 'day', { numeric: 'always' }))

const { data: config } = useQueryConfig.current()
const supportEmailAddress = computed(() => config.value?.support_email || '')

const { subscribe: subscribeRoutePath } = useLinks()
</script>

<template>
  <ChooseWorkspaceModal ref="workspaceModal" />
  <DTModal
    model-value
    :close-on-click-outside="false"
    placement="center"
  >
    <DTModalHeader
      v-if="workspace?.attributes.displayName"
      :use-close="false"
      :title="workspace?.attributes.displayName"
    />
    <DTModalBody>
      <div>
        <span class="mb-2.5 block text-2xl font-semibold">
          {{ capitalize(t('retention-cover.subscription-canceled')) }}
        </span>
        <i18n-t
          keypath="retention-cover.info"
          tag="p"
          class="text-pretty"
        >
          <template #willBeDestroyedIn>
            <strong>{{ t('retention-cover.will-be-destroyed', { timeLeft: timeLeftText }) }}</strong>
          </template>
          <template #supportEmailAdress>
            <DTButton
              variant="link"
              dense
              :to="`mailto:${supportEmailAddress}`"
            >
              {{ supportEmailAddress }}
            </DTButton>
          </template>
          <template #inTheChat>
            <DTButton
              variant="link"
              dense
              @click="$intercom.call('show')"
            >
              {{ t('retention-cover.in-the-chat') }}
            </DTButton>
          </template>
        </i18n-t>
      </div>
    </DTModalBody>
    <DTModalFooter class="flex flex-col !items-start space-y-2.5 !border-none">
      <DTButton
        variant="blue"
        class="w-full"
        :to="subscribeRoutePath()"
        external
      >
        {{ capitalize(t('retention-cover.explore-plans')) }}
      </DTButton>
      <DTButton
        class="w-full"
        @click="workspaceModal?.open()"
      >
        {{ capitalize(t('retention-cover.switch-workspace')) }}
      </DTButton>
      <div class="flex space-x-1.5">
        <i18n-t
          v-if="user"
          keypath="retention-cover.logged-in-as"
          tag="span"
          class="text-sm text-gray-500"
        >
          <template #emailAddress>
            <strong class="text-gray-800">{{ user.attributes.email }}</strong>
          </template>
        </i18n-t>
        <DTButton
          variant="link"
          size="sm"
          dense
          class="text-sm"
          :to="{ name: '2-logout' }"
        >
          {{ t('auth.log-out') }}
        </DTButton>
      </div>
    </DTModalFooter>
  </DTModal>
</template>
